import React, { useEffect, useState } from 'react';
import Select from 'react-select';

/**
 * Load reCAPTCHA v3 script dynamically once we know the siteKey.
 */
function loadRecaptchaScript(siteKey) {
	if (!siteKey) return Promise.reject(new Error('Missing reCAPTCHA site key'));

	const id = 'recaptcha-v3';
	const src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;

	// Already present?
	const existing = document.getElementById(id);
	if (existing) {
		// If same src, we're good. If different, replace it.
		if (existing.getAttribute('src') === src) return Promise.resolve();
		existing.remove();
	}

	return new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.id = id;
		s.src = src;
		s.async = true;
		s.defer = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
		document.head.appendChild(s);
	});
}

/**
 * Wait until grecaptcha is ready (basic polling + grecaptcha.ready callback).
 */
function waitForGrecaptchaReady() {
	return new Promise((resolve, reject) => {
		const start = Date.now();
		const timeoutMs = 10000;

		const tick = () => {
			const g = window.grecaptcha;
			if (g?.ready && g?.execute) {
				g.ready(() => resolve());
				return;
			}
			if (Date.now() - start > timeoutMs) {
				reject(new Error('reCAPTCHA not ready in time'));
				return;
			}
			setTimeout(tick, 50);
		};

		tick();
	});
}

/**
 * Read JSON safely, including application/problem+json.
 */
async function readJsonSafe(res) {
	const ct = (res.headers.get('content-type') || '').toLowerCase();
	if (!ct.includes('json')) return null;
	try {
		return await res.json();
	} catch {
		return null;
	}
}

/**
 * Normalize server validation errors:
 * - { field: ["msg1","msg2"] } -> { field: "msg1" }
 * - strips array index syntax like topicIds[0]
 * - lowercases first letter: Title -> title
 */
function normalizeValidationErrors(errors) {
	if (!errors || typeof errors !== 'object') return {};

	const toUiKey = (k) => {
		if (!k) return k;
		const cleaned = String(k).replace(/\[\d+\]/g, ''); // topicIds[0] -> topicIds
		return cleaned.charAt(0).toLowerCase() + cleaned.slice(1); // Title -> title
	};

	return Object.fromEntries(
		Object.entries(errors).map(([k, v]) => [
			toUiKey(k),
			Array.isArray(v) ? v[0] : String(v),
		])
	);
}

/**
 * Fetch + parse JSON; if response is not ok, throw an Error enriched with status + data.
 */
async function fetchJsonOrThrow(url, options) {
	const res = await fetch(url, options);
	const data = await readJsonSafe(res);

	if (res.ok) return data;

	const err = new Error(data?.title || res.statusText || 'Request failed');
	err.status = res.status;
	err.data = data;
	throw err;
}

export default function CreateRoomForm({ onCreated }) {
	const [title, setTitle] = useState('');
	const [topics, setTopics] = useState([]);
	const [selectedTopics, setSelectedTopics] = useState([]);
	const [numbersOfQuestions, setNumbersOfQuestions] = useState([]);
	const [numberOfQuestions, setNumberOfQuestions] = useState(null);
	const [numbersOfParticipants, setNumbersOfParticipants] = useState([]);
	const [numberOfParticipants, setNumberOfParticipants] = useState(null);

	const [siteKey, setSiteKey] = useState(null);
	const [recaptchaReady, setRecaptchaReady] = useState(false);

	const [status, setStatus] = useState('idle'); // idle, loading-config, calculating, sending, success, error
	const [errors, setErrors] = useState({});
	const [formMessage, setFormMessage] = useState(null);

	useEffect(() => {
		let mounted = true;

		(async () => {
			setStatus('loading-config');
			setFormMessage(null);

			try {
				const cfg = await fetchJsonOrThrow('/api/trivia/new-room-configuration');
				if (!mounted) return;

				setTopics((cfg?.availableTopics || []).map(t => ({
					value: t.key,
					label: t.value
				})));

				setNumbersOfQuestions(cfg?.availableNumberOfQuestions || []);
				setNumbersOfParticipants(cfg?.availableNumberOfParticipants || []);

				// Defaults
				if ((cfg?.availableNumberOfQuestions || []).length > 0) {
					setNumberOfQuestions(cfg.availableNumberOfQuestions[0].key);
				}
				if ((cfg?.availableNumberOfParticipants || []).length > 0) {
					setNumberOfParticipants(cfg.availableNumberOfParticipants[0].key);
				}

				const key = cfg?.recaptchaSiteKey || null;
				setSiteKey(key);
				setRecaptchaReady(false);

				// Load reCAPTCHA script only if key exists
				if (key) {
					try {
						await loadRecaptchaScript(key);
						await waitForGrecaptchaReady();
						if (!mounted) return;
						setRecaptchaReady(true);
					} catch (e) {
						console.warn(e);
						if (!mounted) return;
						setRecaptchaReady(false);
						// Non blocco per forza qui: dipende da policy prodotto.
						// Se per voi è obbligatorio, puoi settare status='error' e messaggio.
					}
				}

				setStatus('idle');
			} catch (err) {
				console.error(err);
				if (!mounted) return;
				setStatus('error');
				setFormMessage('Errore caricamento configurazione');
			}
		})();

		return () => { mounted = false; };
	}, []);

	const solvePoW = async (challenge) => {
		let nonce = 0;
		const encoder = new TextEncoder();

		while (true) {
			const data = encoder.encode(challenge + nonce);
			const hashBuffer = await crypto.subtle.digest('SHA-256', data);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

			if (hashHex.startsWith('0000')) return nonce;

			nonce++;
			if (nonce % 5000 === 0) await new Promise(r => setTimeout(r, 0));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setFormMessage(null);
		setErrors({});

		try {
			// Se reCAPTCHA è previsto e non è pronto, blocco (scelta di prodotto: qui lo tratto come requisito)
			if (siteKey && !recaptchaReady) {
				setFormMessage('AntiBot non pronto: ricarica la pagina o riprova tra poco.');
				setStatus('idle');
				return;
			}

			setStatus('calculating');

			const chJson = await fetchJsonOrThrow('/api/trivia/challenge');
			const challenge = chJson?.challenge;

			const nonce = await solvePoW(challenge);

			setStatus('sending');

			let recaptchaToken = '';
			if (siteKey && recaptchaReady && window.grecaptcha?.execute) {
				try {
					recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'new_room' });
				} catch {
					recaptchaToken = '';
				}
			}

			const body = {
				title,
				numberOfQuestions,
				numberOfParticipants,
				topicIds: selectedTopics,
				recaptchaToken,
				challenge,
				nonce
			};

			const json = await fetchJsonOrThrow('/api/trivia/new-room', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body)
			});

			setStatus('success');
			setFormMessage('Partita creata');
			if (onCreated) onCreated(json);

		} catch (err) {
			// Validation errors server-side (400/422 tipici)
			if ((err.status === 400 || err.status === 422) && err.data?.errors) {
				setErrors(normalizeValidationErrors(err.data.errors));
				setStatus('idle');
				setFormMessage(err.data?.title || 'Controlla i campi evidenziati');
				return;
			}

			if (err.status === 400 && err.data?.code === "recaptcha_failed") {
				setStatus('error');
				setFormMessage('Validazione reCAPTCHA fallita: non è stato possibile accertare che la richiesta provenisse da un utente umano. Riprova nuovamente.');
				return;
			}

			if (err.status === 429) {
				setStatus('error');
				setFormMessage('Si è verificato un errore durante la creazione della partita: hai superato il numero di tentativi/accessi consentito. Ti preghiamo di riprovare tra un minuto.');
				return;
			}

			console.error(err);
			setStatus('error');
			setFormMessage('Si è verificato un errore durante la creazione della partita. Ti preghiamo di riprovare più tardi. Se l\'errore dovesse persistere, puoi segnalare il problema a <a href="mailto:crea@crea.gov.it">crea@crea.gov.it</a>.');
		}
	};

	const submitDisabled =
		status === 'calculating' ||
		status === 'sending' ||
		status === 'loading-config' ||
		(siteKey && !recaptchaReady);

	return (
		<form onSubmit={handleSubmit} className="create-room-form pt-lg-4">
			{formMessage && (
				<div className={`alert ${status === 'success' ? 'alert-success' : 'alert-danger'}`}>
					{formMessage}
				</div>
			)}

			{/* Errori “non-campo” utili (es. recaptchaToken) */}
			{errors.recaptchaToken && (
				<div className="alert alert-danger">
					{errors.recaptchaToken}
				</div>
			)}

			<div className="mb-3">
				<label className="form-label mb-1 fw-bold text-uppercase">
					Scegli un nome per la partita
				</label>
				<input
					className={`form-control ${errors.title ? 'is-invalid' : ''}`}
					value={title}
					onChange={e => {
						setTitle(e.target.value);
						if (errors.title) setErrors(prev => ({ ...prev, title: undefined }));
					}}
				/>
				{errors.title && <div className="field-validation-error">{errors.title}</div>}
			</div>

			<div className="mb-3">
				<label className="form-label mb-1 fw-bold text-uppercase">
					Scegli il numero di domande
				</label>
				<select
					className={`form-select ${errors.numberOfQuestions ? 'is-invalid' : ''}`}
					value={numberOfQuestions ?? ''}
					onChange={e => {
						setNumberOfQuestions(parseInt(e.target.value, 10));
						if (errors.numberOfQuestions) setErrors(prev => ({ ...prev, numberOfQuestions: undefined }));
					}}
				>
					{numbersOfQuestions.map(it => (
						<option key={it.key} value={it.key}>{it.value}</option>
					))}
				</select>
				{errors.numberOfQuestions && (
					<div className="field-validation-error">{errors.numberOfQuestions}</div>
				)}
			</div>

			<div className="mb-3">
				<label className="form-label mb-1 fw-bold text-uppercase">
					Scegli il numero di partecipanti
				</label>
				<select
					className={`form-select ${errors.numberOfParticipants ? 'is-invalid' : ''}`}
					value={numberOfParticipants ?? ''}
					onChange={e => {
						setNumberOfParticipants(parseInt(e.target.value, 10));
						if (errors.numberOfParticipants) setErrors(prev => ({ ...prev, numberOfParticipants: undefined }));
					}}
				>
					{numbersOfParticipants.map(it => (
						<option key={it.key} value={it.key}>{it.value}</option>
					))}
				</select>
				{errors.numberOfParticipants && (
					<div className="field-validation-error">{errors.numberOfParticipants}</div>
				)}
			</div>

			<div className="mb-3">
				<label className="form-label mb-1 fw-bold text-uppercase">
					Scegli gli argomenti
				</label>

				<div className={`topics-list ${errors.topicIds ? 'is-invalid' : ''}`}>
					<Select
						classNamePrefix="react-select"
						isMulti
						options={topics}
						isSearchable={false}
						isClearable
						// se vuoi davvero il menu sempre aperto:
						_menuIsOpen={true}
						placeholder="Seleziona i temi della partita"
						onChange={(opts) => {
							setSelectedTopics((opts || []).map(x => x.value));
							if (errors.topicIds) setErrors(prev => ({ ...prev, topicIds: undefined }));
						}}
						className="react-select-container w-100"
					/>
				</div>
				<div className="mt-1">
					<i className="bi bi-info-circle me-1"></i> Seleziona i temi o lascia vuoto per utilizzare tutti i temi disponibili
				</div>
				{errors.topicIds && (
					<div className="field-validation-error d-block">{errors.topicIds}</div>
				)}
			</div>

			<div className="my-5 text-center">
				<button
					style={{ minWidth: 220 }}
					className="btn btn-primary"
					type="submit"
					disabled={submitDisabled}
				>
					{status === 'loading-config'
						? 'Caricamento...'
						: status === 'calculating'
							? 'Verifica AntiBot...'
							: status === 'sending'
								? 'Invio in corso...'
								: siteKey && !recaptchaReady
									? 'AntiBot non pronto...'
									: 'Crea Partita'}
				</button>
			</div>
		</form>
	);
}
